import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCartStore } from '../store/cart.store';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardTitle } from '../components/ui/card';
import { ProductCard } from '../components/shop/ProductCard';
import { productService } from '../services/product.service';
import { getAccessToken } from '../lib/api-client';
import { X, Plus, Minus, ShoppingBag, ArrowLeft, CreditCard, Heart } from 'lucide-react';
import { useToast } from '../components/ui/ToastProvider';

const WishlistSection: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const hasToken = Boolean(getAccessToken());
  const { data: wishlistItems = [], isLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: productService.getWishlist,
    enabled: hasToken,
  });
  const removeMutation = useMutation({
    mutationFn: productService.removeFromWishlist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      showToast('Removed from wishlist', 'info');
    },
    onError: () => showToast('Failed to remove from wishlist', 'error'),
  });

  if (!hasToken || isLoading || wishlistItems.length === 0) return null;

  return (
    <section className="mt-12 border-t border-[rgb(var(--border))] pt-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            <h2 className="text-2xl font-bold text-[rgb(var(--foreground))]">Your wishlist</h2>
          </div>
          <p className="mt-1 text-[rgb(var(--light-secondary))]">Saved items ready for your next order</p>
        </div>
        <Link to="/wishlist" className="text-sm font-medium text-primary hover:underline">View all</Link>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {wishlistItems.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onWishlistToggle={(productId) => removeMutation.mutate(productId)}
            inWishlist
          />
        ))}
      </div>
    </section>
  );
};

export const CartPage: React.FC = () => {
  const { items, removeItem, updateQuantity, clearCart, getTotalItems, getSubtotal, getTotal } = useCartStore();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleRemove = (id: number) => {
    removeItem(id);
    showToast('Item removed from cart', 'info');
  };

  const handleCheckout = () => {
    if (items.length === 0) {
      showToast('Your cart is empty', 'error');
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <ShoppingBag className="mx-auto h-16 w-16 text-[rgb(var(--light-secondary))]" />
          <h2 className="mt-4 text-2xl font-bold text-[rgb(var(--foreground))]">Your cart is empty</h2>
          <p className="mt-2 text-[rgb(var(--light-secondary))]">
            Browse our shop and add items you love
          </p>
          <Link to="/shop">
            <Button variant="primary" className="mt-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Continue Shopping
            </Button>
          </Link>
        </div>
        <WishlistSection />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-[rgb(var(--foreground))]">Shopping Cart</h1>
      <p className="text-[rgb(var(--light-secondary))]">{getTotalItems()} items in your cart</p>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
                <img
                  src={item.image || '/placeholder-product.png'}
                  alt={item.name}
                  className="h-24 w-24 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-[rgb(var(--foreground))]">{item.name}</h3>
                      <p className="text-sm text-[rgb(var(--light-secondary))]">
                        GHS {(item.price / 100).toFixed(2)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemove(item.id)}
                      className="text-[rgb(var(--light-secondary))] hover:text-red-500 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center space-x-3">
                    <div className="flex items-center rounded-lg border border-[rgb(var(--border))]">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="px-3 py-1 hover:bg-[rgb(var(--border))] transition-colors"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-12 text-center text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="px-3 py-1 hover:bg-[rgb(var(--border))] transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <span className="font-medium text-primary">
                      GHS {((item.price * item.quantity) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-between">
            <Link to="/shop">
              <Button variant="secondary">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Continue Shopping
              </Button>
            </Link>
            <Button variant="ghost" onClick={clearCart} className="text-red-500 hover:text-red-600">
              Clear Cart
            </Button>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card>
            <CardTitle>Order Summary</CardTitle>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-[rgb(var(--light-secondary))]">Subtotal</span>
                <span>GHS {(getSubtotal() / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[rgb(var(--light-secondary))]">Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[rgb(var(--light-secondary))]">Tax</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="border-t border-[rgb(var(--border))] pt-4">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-primary">GHS {(getTotal() / 100).toFixed(2)}</span>
                </div>
              </div>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleCheckout}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Proceed to Checkout
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <WishlistSection />
    </div>
  );
};